// Modèles de contrat de la société (administrateur) : un modèle par type de client. Les clauses obligatoires ne se retirent
// pas ; un texte légal reproduit (verrouillé) ne se modifie pas ; les clauses facultatives viennent de la bibliothèque.
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { Alerte, Badge, Bouton, Chargement, Champ, ChampChoix, ChampTexte, Dialogue, EntetePage, EtatErreur, Panneau, useConfirmation, useNotifier } from '../../components/ui.tsx';
import { GardeContrats } from './commun.tsx';
import type { Modele, ModeleClause } from './types.ts';

const SEGMENTS: Record<Modele['segment'], string> = {
  entreprise_collectivite: 'Entreprises et collectivités',
  syndicat_copro: 'Syndicats de copropriété',
};
const SOURCES: Record<string, string> = { obligation: 'Obligation légale', a_confirmer: 'À confirmer par votre conseil', usage: 'Usage de la profession' };

export function PageModelesContrats() {
  return <GardeContrats><Modeles /></GardeContrats>;
}

function Modeles() {
  const { peut } = useSession();
  const q = useQuery({ queryKey: ['contrats-modeles'], queryFn: () => api.get<{ modeles: Modele[] }>('/api/contrats-modeles'), enabled: peut('contrats.modeles') });
  if (!peut('contrats.modeles')) {
    return <EtatErreur erreur={new Error('La gestion des modèles de contrat est réservée à l’administrateur.')} />;
  }
  return (
    <div className="pile-l">
      <EntetePage titre="Modèles de contrat" fil={<Link to="/contrats">Contrats de maintenance</Link>}
        sousTitre="Chaque nouveau contrat reprend le modèle de son type de client. Les clauses obligatoires restent toujours dans le contrat." />
      {q.isLoading && <Chargement />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data?.modeles.map((m) => <PanneauModele key={m.id} m={m} />)}
    </div>
  );
}

function PanneauModele({ m }: { m: Modele }) {
  const qc = useQueryClient(); const notifier = useNotifier(); const { element, confirmer } = useConfirmation();
  const [edition, setEdition] = useState<ModeleClause | null>(null);
  const [ajout, setAjout] = useState('');
  const recharger = () => qc.invalidateQueries({ queryKey: ['contrats-modeles'] });
  const retirer = useMutation({
    mutationFn: (id: string) => api.del(`/api/contrats-modeles/clauses/${id}`),
    onSuccess: () => { recharger(); notifier('Clause retirée du modèle.'); }, onError: (e) => notifier(messageErreur(e), true),
  });
  const ajouter = useMutation({
    mutationFn: (code: string) => api.post(`/api/contrats-modeles/${m.id}/clauses`, { code }),
    onSuccess: () => { setAjout(''); recharger(); notifier('Clause ajoutée au modèle.'); }, onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Panneau titre={`${SEGMENTS[m.segment]} — ${m.nom}`} actions={<span className="petit discret">version {m.version}</span>}>
      <div className="pile">
        <ol className="cm-clauses">
          {m.clauses.map((c) => (
            <li key={c.id} className="cm-clause">
              <div className="cm-clause-tete">
                <span className="cm-clause-titre">{c.titre}</span>
                <span className="int-ligne-badges">
                  {c.obligatoire && <Badge ton="accent" icone={false}>Obligatoire</Badge>}
                  {c.texte_verrouille && <Badge ton="neutre" icone={<Lock aria-hidden />}>Texte légal verrouillé</Badge>}
                  <Badge ton={c.statut_source === 'a_confirmer' ? 'ambre' : 'neutre'} icone={false}>{SOURCES[c.statut_source] ?? c.statut_source}</Badge>
                </span>
              </div>
              <p className="cm-texte petit">{c.texte}</p>
              {c.source && <span className="tres-petit discret">Source : {c.source}</span>}
              <div className="barre-actions">
                <Bouton compact variante="fantome" icone={<Pencil aria-hidden />} onClick={() => setEdition(c)}>{c.texte_verrouille ? 'Renommer' : 'Modifier'}</Bouton>
                {!c.obligatoire && (
                  <Bouton compact variante="fantome" icone={<Trash2 aria-hidden />} chargement={retirer.isPending && retirer.variables === c.id}
                    onClick={async () => { if (await confirmer('Retirer la clause', `« ${c.titre} » ne figurera plus dans les prochains contrats. Les contrats existants ne changent pas.`, 'Retirer')) retirer.mutate(c.id); }}>
                    Retirer
                  </Bouton>
                )}
              </div>
            </li>
          ))}
        </ol>
        {m.bibliotheque.length > 0 && (
          <div className="cm-ajout-clause">
            <ChampChoix libelle="Ajouter une clause de la bibliothèque" value={ajout} onChange={(e) => setAjout(e.target.value)} vide="Choisir une clause"
              options={m.bibliotheque.map((b) => ({ valeur: b.code, libelle: b.titre }))} />
            <Bouton icone={<Plus aria-hidden />} disabled={!ajout} chargement={ajouter.isPending} onClick={() => ajouter.mutate(ajout)}>Ajouter</Bouton>
          </div>
        )}
        <Alerte ton="info" titre="Pied de modèle">{m.pied_de_modele}</Alerte>
      </div>
      <DialogueClause clause={edition} segment={m.segment} fermer={() => setEdition(null)} />
      {element}
    </Panneau>
  );
}

function DialogueClause({ clause, segment, fermer }: { clause: ModeleClause | null; segment: Modele['segment']; fermer: () => void }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const [titre, setTitre] = useState('');
  const [texte, setTexte] = useState('');
  useEffect(() => { if (clause) { setTitre(clause.titre); setTexte(clause.texte); } }, [clause]);
  const m = useMutation({
    mutationFn: () => api.patch(`/api/contrats-modeles/clauses/${clause!.id}`, {
      ...(titre.trim() !== clause!.titre ? { titre: titre.trim() } : {}),
      ...(!clause!.texte_verrouille && texte.trim() !== clause!.texte ? { texte: texte.trim() } : {}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['contrats-modeles'] }); fermer(); notifier('Clause enregistrée.'); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  const change = !!clause && (titre.trim() !== clause.titre || (!clause.texte_verrouille && texte.trim() !== clause.texte));
  return (
    <Dialogue titre="Modifier la clause" ouvert={!!clause} fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" disabled={!change || !titre.trim() || !texte.trim()} chargement={m.isPending} onClick={() => m.mutate()}>Enregistrer</Bouton></>}>
      {clause && (
        <div className="pile">
          <Champ libelle="Titre" value={titre} onChange={(e) => setTitre(e.target.value)} maxLength={200} />
          {clause.texte_verrouille
            ? <Alerte ton="info" titre="Texte légal verrouillé">Ce texte reproduit une disposition légale : seul le titre se modifie.</Alerte>
            : <ChampTexte libelle="Texte" value={texte} onChange={(e) => setTexte(e.target.value)} rows={8} maxLength={20000}
                aide={segment === 'syndicat_copro' ? 'Pour un syndicat de copropriété, certaines formulations sont refusées (clauses interdites).' : 'Les formulations interdites par la loi sont refusées à l’enregistrement.'} />}
        </div>
      )}
    </Dialogue>
  );
}
